import React, { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams, Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ArrowLeft, Link2, Image as ImageIcon, ExternalLink, MapPin, UserPlus, AlertTriangle, Loader2, Plus,
} from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useT } from "@/i18n";
import { formatDateTime, formatDate, formatAddress } from "@/lib/format";
import { Amount } from "@/components/common/Amount";
import {
  woStatusTone, semaforoTone, prioridadTone, billingTone, dueRelative, BILLING_STATUSES,
} from "@/lib/wo";
import { PageHeader } from "@/components/common/PageHeader";
import { StatusChip } from "@/components/common/StatusChip";
import { Section } from "@/features/workorders/Section";
import { LineItemsPanel } from "@/features/workorders/LineItemsPanel";
import { EvidencePanel } from "@/features/workorders/EvidencePanel";
import { PreinspectionPanel } from "@/features/workorders/PreinspectionPanel";
import { TimelinePanel } from "@/features/workorders/TimelinePanel";
import { AssignDialog } from "@/features/workorders/AssignDialog";
import { TransitionDialog } from "@/features/workorders/TransitionDialog";
import { QboInvoiceSection } from "@/features/workorders/QboInvoiceSection";
import { PurchaseDialog } from "@/features/finance/PurchaseDialog";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const Row = ({ label, value, testId }) => (
  <div className="flex items-start gap-3 border-b border-line py-2 last:border-0">
    <span className="w-[136px] shrink-0 font-mono text-[11px] uppercase tracking-[0.06em] text-ink-tertiary">
      {label}
    </span>
    <span data-testid={testId} className="min-w-0 flex-1 text-[12.5px] text-ink">
      {value ?? "—"}
    </span>
  </div>
);

export default function WorkOrderDetailPage() {
  const { id } = useParams();
  const { t } = useT();
  const { user, can } = useAuth();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [params, setParams] = useSearchParams();
  const [assignOpen, setAssignOpen] = useState(false);
  const [transition, setTransition] = useState(null);
  const [cardBusy, setCardBusy] = useState(false);
  const [purchaseOpen, setPurchaseOpen] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ["work-order", id],
    queryFn: async () => (await api.get(`/work-orders/${id}`)).data,
  });

  const canSeePurchases = can("finance", "read");
  const { data: purchasesData } = useQuery({
    queryKey: ["purchases", { work_order_id: id }],
    queryFn: async () => (await api.get("/purchases", { params: { work_order_id: id, page_size: 100 } })).data,
    enabled: canSeePurchases,
  });

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ["work-order", id] });
    qc.invalidateQueries({ queryKey: ["work-orders"] });
    qc.invalidateQueries({ queryKey: ["wo-summary"] });
    qc.invalidateQueries({ queryKey: ["purchases"] });
  };

  useEffect(() => {
    if (params.get("assign") === "1") {
      setAssignOpen(true);
      params.delete("assign");
      setParams(params, { replace: true });
    }
  }, [params, setParams]);

  if (isLoading || !data) {
    return (
      <div className="p-6" data-testid="wo-detail-loading">
        <div className="loading-bar" />
        <div className="mt-5 space-y-2.5">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="h-[13px] w-full bg-surface-sunken" />
          ))}
        </div>
      </div>
    );
  }

  const { wo, lineas, evidencia, observaciones, notas, timeline, factura_qbo, preinspecciones } = data;
  const canUpdate = can("work_orders", "update");
  const canApprove = can("work_orders", "approve");
  const isInternal = ["owner", "socio_operativo", "operaciones", "ventas", "supervisor"].includes(user?.rol);

  const copyLink = async () => {
    if (!wo.public_url) return;
    try {
      await navigator.clipboard.writeText(wo.public_url);
    } catch (e) {
      /* clipboard bloqueado */
    }
    toast.success(t("wo.detail.copied"));
  };

  const generateCard = async () => {
    setCardBusy(true);
    try {
      const { data: res } = await api.post(`/work-orders/${wo.id}/card`);
      toast.success(t("wo.detail.card_generated"));
      window.open(res.url, "_blank");
      refresh();
    } catch (e) {
      toast.error(apiError(e, t("app.error")));
    } finally {
      setCardBusy(false);
    }
  };

  const changeBilling = async (value) => {
    try {
      await api.patch(`/work-orders/${wo.id}/billing`, { billing_status: value });
      toast.success(t("app.saved"));
      refresh();
    } catch (e) {
      toast.error(apiError(e, t("app.error")));
    }
  };

  const showMoney = wo.sub_cost_total_cents !== undefined || wo.client_price_total_cents !== undefined;

  return (
    <>
      <PageHeader
        testId="wo-detail-header"
        breadcrumb={
          <button
            data-testid="wo-back"
            onClick={() => navigate("/ordenes")}
            className="mb-1.5 inline-flex items-center gap-1 text-[12px] text-ink-secondary hover:text-ink"
          >
            <ArrowLeft className="h-3.5 w-3.5" /> {t("wo.title")}
          </button>
        }
        title={
          <span className="flex flex-wrap items-center gap-2.5">
            <span className="font-mono tabular-nums">{wo.code}</span>
            <StatusChip tone={woStatusTone(wo.status)} label={t(`woStatus.${wo.status}`)} testId="wo-detail-status" />
            {wo.semaforo !== "gris" && (
              <StatusChip
                tone={semaforoTone(wo.semaforo)}
                testId="wo-detail-semaforo"
                label={`${formatDate(wo.due_at)} · ${dueRelative(wo.due_at)}`}
              />
            )}
            <StatusChip tone={prioridadTone(wo.prioridad)} label={t(`priority.${wo.prioridad}`)} />
            {wo.pago_semaforo !== undefined && wo.pago_semaforo && (
              <StatusChip
                tone={semaforoTone(wo.pago_semaforo)}
                testId="wo-detail-pago-semaforo"
                label={`${t("wo.pago_sub")}: ${t(`payment_semaforo.${wo.pago_semaforo}`)}`}
              />
            )}
          </span>
        }
        subtitle={
          <>
            <Link to={`/edificios/${wo.property_id}`} className="font-medium text-ink hover:text-brand hover:underline">
              {wo.property_nombre}
            </Link>
            {wo.unit_etiqueta ? ` · ${wo.unit_etiqueta}` : ""} · {wo.service_nombre}
          </>
        }
        actions={
          <div className="flex flex-wrap items-center justify-end gap-2">
            {(wo.transiciones || []).map((x) => (
              <Button
                key={x.destino}
                variant={x.destino === "cancelada" ? "outline" : "default"}
                data-testid={`wo-action-${x.destino}`}
                onClick={() => {
                  if (x.destino === "asignada") setAssignOpen(true);
                  else setTransition(x);
                }}
                className={cn_action(x.destino)}
              >
                {x.label}
              </Button>
            ))}
            {canUpdate && wo.status !== "nueva" && wo.status !== "cerrada" && (
              <Button variant="outline" data-testid="wo-reassign" onClick={() => setAssignOpen(true)} className="h-8">
                <UserPlus className="mr-1.5 h-3.5 w-3.5" /> {t("wo.assign.reassign")}
              </Button>
            )}
            <Button variant="outline" data-testid="wo-copy-link" onClick={copyLink} className="h-8">
              <Link2 className="mr-1.5 h-3.5 w-3.5" /> {t("wo.detail.copy_link")}
            </Button>
            {/* Fix T (2026-10-05): antes estaba debajo de la factura y parecía el link de la
                factura; es el portal del subcontratista. */}
            {wo.public_url && (
              <Button asChild variant="outline" className="h-8">
                <a href={wo.public_url} target="_blank" rel="noreferrer" data-testid="wo-open-public">
                  <ExternalLink className="mr-1.5 h-3.5 w-3.5" /> {t("wo.detail.open_public")}
                </a>
              </Button>
            )}
            <Button variant="outline" data-testid="wo-generate-card" disabled={cardBusy} onClick={generateCard} className="h-8">
              {cardBusy ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <ImageIcon className="mr-1.5 h-3.5 w-3.5" />}
              {t("wo.detail.card")}
            </Button>
          </div>
        }
      />

      <div className="flex-1 overflow-y-auto" data-testid="wo-detail-page">
        <div className="grid divide-y divide-line xl:grid-cols-3 xl:divide-x xl:divide-y-0">
          <div className="space-y-8 px-5 py-5">
            <Section title={t("wo.detail.data")}>
              <Row label={t("wo.property")} value={wo.property_nombre} />
              <Row
                label={t("app.address")}
                value={
                  <a
                    href={`https://maps.google.com/?q=${encodeURIComponent(formatAddress(wo.property_direccion))}`}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-1 text-brand hover:underline"
                  >
                    <MapPin className="h-3 w-3" />
                    {formatAddress(wo.property_direccion)}
                  </a>
                }
              />
              <Row label={t("wo.unit")} value={wo.unit_etiqueta} />
              <Row label={t("wo.service")} value={wo.service_nombre} />
              <Row label={t("wo.sub")} value={wo.sub_nombre} testId="wo-detail-sub" />
              <Row label={t("wo.responsable")} value={wo.responsable_nombre} />
              <Row label={t("wo.due")} value={<span className="font-mono">{formatDateTime(wo.due_at)}</span>} />
              {wo.cita_texto && <Row label={t("wo.detail.appointment")} value={wo.cita_texto} testId="wo-detail-appointment" />}
              <Row label={t("wo.detail.created_by")} value={`${wo.created_by_nombre || "—"} · ${formatDateTime(wo.created_at)}`} />
              {wo.accepted_at && <Row label={t("wo.detail.accepted")} value={`${formatDateTime(wo.accepted_at)}${wo.medio_confirmacion ? ` · ${wo.medio_confirmacion}` : ""}`} />}
              {wo.started_at && <Row label={t("wo.detail.started")} value={formatDateTime(wo.started_at)} />}
              {wo.finished_at && <Row label={t("wo.detail.finished")} value={formatDateTime(wo.finished_at)} />}
              {wo.approved_at && <Row label={t("wo.detail.approved")} value={formatDateTime(wo.approved_at)} />}
              {wo.closed_at && <Row label={t("wo.detail.closed")} value={formatDateTime(wo.closed_at)} />}
              {wo.cancelled_at && <Row label={t("wo.detail.cancelled")} value={wo.motivo_cancelacion} />}
              {wo.motivo_pausa && <Row label={t("woStatus.en_pausa")} value={wo.motivo_pausa} />}
              {!!wo.correction_cycle && <Row label={t("wo.detail.cycle")} value={wo.correction_cycle} />}
              {wo.descripcion && <Row label={t("app.detail")} value={wo.descripcion} />}
            </Section>

            {wo.notas_acceso && (
              <div className="border-l-2 border-brand bg-brand-soft px-4 py-3" data-testid="wo-access-notes">
                <h3 className="font-mono text-[11px] font-semibold uppercase tracking-[0.06em] text-brand">
                  {t("wo.detail.access_notes")}
                </h3>
                <p className="mt-1 text-[12.5px] leading-relaxed text-ink">{wo.notas_acceso}</p>
              </div>
            )}

            {showMoney && (
              <Section title={t("wo.detail.money")} testId="wo-money">
                <div className="grid grid-cols-2 gap-x-6 gap-y-4">
                  {wo.sub_cost_total_cents !== undefined && (
                    <div>
                      <p className="font-mono text-[11px] uppercase tracking-[0.06em] text-ink-tertiary">{t("wo.cost")}</p>
                      <Amount cents={wo.sub_cost_total_cents} testId="wo-money-cost" className="mt-1 text-[19px]" />
                    </div>
                  )}
                  {wo.insumos_cents !== undefined && (
                    <div>
                      <p className="font-mono text-[11px] uppercase tracking-[0.06em] text-ink-tertiary">{t("wo.insumos")}</p>
                      <Amount cents={wo.insumos_cents} testId="wo-money-insumos" className="mt-1 text-[19px]" />
                    </div>
                  )}
                  {wo.costo_total_cents !== undefined && (
                    <div>
                      <p className="font-mono text-[11px] uppercase tracking-[0.06em] text-ink-tertiary">{t("wo.cost_total")}</p>
                      <Amount cents={wo.costo_total_cents} testId="wo-money-cost-total" className="mt-1 text-[19px]" />
                    </div>
                  )}
                  {wo.client_price_total_cents !== undefined && (
                    <div>
                      <p className="font-mono text-[11px] uppercase tracking-[0.06em] text-ink-tertiary">{t("wo.price")}</p>
                      <Amount cents={wo.client_price_total_cents} testId="wo-money-price" className="mt-1 text-[19px]" />
                    </div>
                  )}
                  {wo.margen_cents !== undefined && (
                    <div>
                      <p className="font-mono text-[11px] uppercase tracking-[0.06em] text-ink-tertiary">{t("wo.margin")}</p>
                      <p className="mt-1 flex items-baseline gap-1.5" data-testid="wo-money-margin">
                        <Amount cents={wo.margen_cents} className={cn_margin(wo.margen_cents) + " text-[19px]"} />
                        <span className="font-mono text-[12px] text-ink-tertiary">
                          {wo.margen_pct === null || wo.margen_pct === undefined ? t("wo.margin_na") : `${wo.margen_pct}%`}
                        </span>
                      </p>
                    </div>
                  )}
                  <div>
                    <p className="font-mono text-[11px] uppercase tracking-[0.06em] text-ink-tertiary">{t("wo.billing")}</p>
                    {can("finance", "update") ? (
                      <Select value={wo.billing_status} onValueChange={changeBilling}>
                        <SelectTrigger data-testid="wo-billing-select" className="mt-1 h-8 text-[12.5px]">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {BILLING_STATUSES.map((b) => (
                            <SelectItem key={b} value={b}>{t(`billing.${b}`)}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ) : (
                      <div className="mt-1.5"><StatusChip tone={billingTone(wo.billing_status)} label={t(`billing.${wo.billing_status}`)} /></div>
                    )}
                  </div>
                </div>
                {wo.sub_cost_agreed_by_nombre && (
                  <p className="mt-3 border-t border-line pt-2 text-[11.5px] text-ink-secondary">
                    {t("wo.assign.agreed_by")} {wo.sub_cost_agreed_by_nombre} · {formatDateTime(wo.sub_cost_agreed_at)}
                  </p>
                )}
                {wo.sub_cost_modificado && (
                  <p data-testid="wo-cost-modified" className="mt-2 flex items-center gap-1.5 border-l-2 border-state-warn bg-state-warn-soft px-2.5 py-1.5 text-[11.5px] font-medium text-state-warn">
                    <AlertTriangle className="h-3.5 w-3.5" />
                    {t("wo.assign.modified")}
                  </p>
                )}
                {!wo.client_price_total_cents && (
                  <p className="mt-2 flex items-center gap-1.5 border-l-2 border-state-critical bg-state-critical-soft px-2.5 py-1.5 text-[11.5px] font-medium text-state-critical">
                    <AlertTriangle className="h-3.5 w-3.5" />
                    {t("wo.detail.no_price")}
                  </p>
                )}
                {wo.precio_excepcion && (
                  <p data-testid="wo-price-exception" className="mt-2 flex items-center gap-1.5 border-l-2 border-state-warn bg-state-warn-soft px-2.5 py-1.5 text-[11.5px] font-medium text-state-warn">
                    <AlertTriangle className="h-3.5 w-3.5" />
                    {t("wo.detail.price_exception")}{wo.precio_excepcion_motivo ? `: ${wo.precio_excepcion_motivo}` : ""}
                  </p>
                )}
              </Section>
            )}

            {canSeePurchases && (
              <Section
                title={t("finance.purchases.detail_block.title")}
                testId="wo-purchases"
                action={can("finance", "create") && (
                  <Button size="sm" variant="outline" data-testid="wo-add-purchase" onClick={() => setPurchaseOpen(true)} className="h-7 px-2 text-[12px]">
                    <Plus className="mr-1 h-3.5 w-3.5" /> {t("finance.purchases.new")}
                  </Button>
                )}
              >
                {!purchasesData?.items?.length ? (
                  <p className="py-4 text-center text-[12.5px] text-ink-tertiary">{t("wo.detail.no_purchases")}</p>
                ) : (
                  <ul className="space-y-1.5">
                    {purchasesData.items.map((p) => (
                      <li key={p.id} data-testid={`wo-purchase-row-${p.id}`}
                        className="flex items-center justify-between gap-2 border-b border-line py-1.5 text-[12px]">
                        <span className="truncate text-ink-secondary">
                          {formatDate(p.fecha)} · {p.proveedor || p.descripcion || "—"}
                          {p.anulado && <span className="ml-1.5 text-state-critical">({t("finance.payments.voided")})</span>}
                        </span>
                        <Amount cents={p.monto_cents} className="text-[12px]" />
                      </li>
                    ))}
                  </ul>
                )}
              </Section>
            )}

            <LineItemsPanel wo={wo} lineas={lineas} evidencia={evidencia} canUpdate={canUpdate} canApprove={canApprove} onDone={refresh} />

            <QboInvoiceSection wo={wo} factura={factura_qbo} onDone={refresh} />
          </div>

          <div className="space-y-8 px-5 py-5">
            <PreinspectionPanel wo={wo} preinspecciones={preinspecciones} canUpload={canUpdate} onDone={refresh} />
            <EvidencePanel wo={wo} evidencia={evidencia} observaciones={observaciones} canUpload={canUpdate} canObserve={canApprove} onDone={refresh} />
          </div>

          <div className="space-y-8 px-5 py-5">
            <TimelinePanel woId={wo.id} timeline={timeline} notas={notas} canNote={isInternal && canUpdate} onDone={refresh} />
          </div>
        </div>
      </div>

      <AssignDialog open={assignOpen} onOpenChange={setAssignOpen} wo={wo} onDone={refresh} />
      <TransitionDialog woId={wo.id} wo={wo} transition={transition} onOpenChange={(v) => !v && setTransition(null)} onDone={refresh} />
      <PurchaseDialog open={purchaseOpen} onOpenChange={setPurchaseOpen} workOrderId={wo.id} invalidateKeys={[["purchases", { work_order_id: id }], ["work-order", id]]} />
    </>
  );
}

function cn_action(destino) {
  return destino === "cancelada" ? "h-8 border-state-critical text-state-critical hover:bg-state-critical-soft" : "h-8 bg-ink text-paper hover:opacity-90";
}

function cn_margin(cents) {
  return cents < 0 ? "text-state-critical" : "text-state-ok";
}
